import React, { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiskIq } from '@tri-opra/riskiq-web';
import '@tri-opra/riskiq-web/styles.css';
import CommentsSideBar from '@components-clean/core/comments-side-bar';
import { FullPageInfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { IFormData, ProtocolReviewContext } from '@pages-clean/risk-assessment/risk-iq/interfaces/context';
import { useRiskIqAvailability } from '@pages-clean/risk-assessment/risk-iq/review/use-risk-iq-availability';
import {
    SelectedRiskIqItem,
    useRiskIqEventHandler,
} from '@pages-clean/risk-assessment/risk-iq/review/use-risk-iq-event-handler';
import { NullableStudy } from '@contexts/local-state-context';
import { UserContext, Permissions } from '@contexts/user-context';
import Constants from '@utils/constants';
import { getIsActiveField } from '@utils/convertor';
import Settings from '@settings/settings';
import Modules from '@config/modules';
import { LoggedInPageProps } from '@config/routes/interfaces';
import './index.scss';

interface IProtocolReviewProps {
    study: NullableStudy | undefined;
}

interface ISelectedReviewState {
    isFinished: boolean;
}

const Content = ({ study }: IProtocolReviewProps) => {
    const [formData, setFormData] = useState<IFormData>({
        study,
    });
    const { user, token } = useContext(UserContext);
    const { state, dispatch } = usePageToolbar();

    const { componentHasChanges } = useUnsavedChangesContext();

    // Holds the CtQ/Risk type + OPRA Mongo id resolved from the last selection-changed event, for
    // feeding to the CtQ/Risk comments component.
    const [selectedItem, setSelectedItem] = useState<SelectedRiskIqItem | null>(null);
    const [selectedReviewState, setSelectedReviewState] = useState<ISelectedReviewState | null>(null);
    const [commentsReloadKey, setCommentsReloadKey] = useState<number>(0);

    const StudyHasRAM = study && getIsActiveField('RAM', study.modules);

    const protocolReviewProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            selectedItem,
        }),
        [formData, setFormData, selectedItem],
    );

    const { t } = useTranslation();

    const apiOrigin = Settings.api.riskIqApiOrigin() || undefined;
    const bearerToken = token ?? undefined;
    const riskIqAvailability = useRiskIqAvailability(apiOrigin, bearerToken);

    const commentsWriteEnabled =
        !!user?.canWrite?.({
            module: Constants.Modules.RAM,
            element: Constants.DataElements.RiskAssessmentRiskIQComments,
            permission: Permissions.ReadWrite,
        }) && selectedReviewState?.isFinished === false;

    let commentEntityType: 'RiskIQRisk' | 'RiskIQCtQ' = 'RiskIQRisk';
    let commentMessageHubRelativeEndpoint: 'risk-hub' | 'ctq-hub' = 'risk-hub';
    if (selectedItem?.type === 'ctq') {
        commentEntityType = 'RiskIQCtQ';
        commentMessageHubRelativeEndpoint = 'ctq-hub';
    }

    const hasCommentsPageAccess = user?.can?.({
        module: Modules.RiskAssessment.value,
        element: Constants.PageElements.RAMRiskIQComments,
        permission: Permissions.Enabled,
    });

    const showCommentsSidebar = selectedReviewState && state.commentsToggle && hasCommentsPageAccess;

    useEffect(() => {
        setFormData({
            ...formData,
            study,
        });
    }, [study?.id]);

    useLayoutEffect(() => {
        if (!hasCommentsPageAccess) {
            return;
        }

        const hasCommentsToggle = state.components.includes('commentsToggle');
        if (selectedReviewState && !hasCommentsToggle) {
            dispatch({ type: 'showCommentsToggle', ...state });
            return;
        }
        if (!selectedReviewState && hasCommentsToggle) {
            dispatch({ type: 'hideCommentsToggle', ...state });
        }
    }, [state.components, selectedReviewState]);

    const handleRiskIqEvent = useRiskIqEventHandler(
        study?.id ?? undefined,
        (item: SelectedRiskIqItem | null) => setSelectedItem(item),
        (isFinished) => setSelectedReviewState({ isFinished }),
        () => setSelectedReviewState(null),
        () => {
            if (componentHasChanges(ComponentNames.commentsSideBar)) {
                setCommentsReloadKey((prevKey) => prevKey + 1);
            }
        },
        () => setSelectedReviewState({ isFinished: true }),
    );

    if (!StudyHasRAM) return <FullPageInfoField message={t('home:pageDataErrors:cannotAccessModule')} type="Info" />;

    if (riskIqAvailability === 'checking') return <Loading />;

    // RiskIQ's backend is switched off for this environment (independently of the UI flag).
    if (riskIqAvailability === 'unavailable')
        return <FullPageInfoField message={t('ram:riskIQ:unavailable')} type="Info" />;

    return (
        <div className="protocolReview-container">
            <ProtocolReviewContext.Provider value={protocolReviewProviderValue}>
                <div
                    style={{
                        height: '100%',
                        display: 'grid',
                        gridTemplateColumns: showCommentsSidebar ? 'minmax(0, 1fr) auto' : '100%',
                    }}
                >
                    <div style={{ minWidth: 0, height: '100%', overflowY: 'auto' }}>
                        <RiskIq
                            mode="opra-embedded"
                            bearerToken={bearerToken}
                            apiOrigin={apiOrigin}
                            currentUserEmail={user?.email}
                            hostHasUnsavedSelectionChanges={componentHasChanges(ComponentNames.commentsSideBar)}
                            onAppEvent={handleRiskIqEvent}
                            studyId={study.id}
                        />
                    </div>
                    {showCommentsSidebar && (
                        <div style={{ display: 'flex', height: '100%' }}>
                            <CommentsSideBar
                                key={commentsReloadKey}
                                studyId={study.id}
                                entityType={commentEntityType}
                                messageHubRelativeEndpoint={commentMessageHubRelativeEndpoint}
                                parentId={selectedItem?.id ?? undefined}
                                noParentSelectedMessage={t('ram:riskIQ.noCommentsParentRecordSelected')}
                                writeEnabled={commentsWriteEnabled}
                            />
                        </div>
                    )}
                </div>
            </ProtocolReviewContext.Provider>
        </div>
    );
};

const ProtocolReviewWrapper = ({ study }: IProtocolReviewProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const ProtocolReview = ({ study }: LoggedInPageProps) => <ProtocolReviewWrapper study={study} />;

export default ProtocolReview;
